// frontend/src/App.tsx
// Application shell, global navigation, auth gates, and route table.

import React, { useState, useEffect, ReactNode, Suspense, lazy } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { mixpanelTrack } from './services/mixpanel';
import { saveIntendedPath } from './utils/intendedRedirect';
import Spinner from './components/common/Spinner';
import SEO from './components/common/SEO';
import { Toaster } from 'react-hot-toast';
import { ConfirmDialogProvider } from './components/common/ConfirmDialog';
import {
  buildDfsContestSectionHref,
  getDfsBuilderIdentifierFromPath,
  getDfsContestIdentifierFromPath,
} from './utils/dfsContestNavigation';

// --- Import Navigation Components ---
import GeneralBottomNav from './components/nav/GeneralBottomNav';
import DfsContestBottomNav from './components/nav/DfsContestBottomNav';
import LeagueTopTabs from './components/league/LeagueTopTabs';

// --- Import Existing Page Components ---
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ScoringInfoPage from './pages/ScoringInfoPage';
import FilmRoomPage from './pages/FilmRoomPage';
import ContestsPage from './pages/ContestsPage';
import ContestRulesPage from './pages/ContestRulesPage';
import SupportPage from './pages/SupportPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import BarLandingPage from './pages/BarLandingPage';
import VenuePage from './pages/VenuePage';
import VenueCoBrand from './components/common/VenueCoBrand';
import { VENUE_SLUGS } from './utils/venues';
import DfsLineupBuilderPage from './pages/DfsLineupBuilderPage';
import DfsContestPage from './pages/DfsContestPage';
import RootRedirector from './components/dfs/RootRedirector';
import PublicPlayerStatsPage from './pages/PublicPlayerStatsPage';
import GameLogLeaderboardPage from './pages/GameLogLeaderboardPage';
import AdminPage from './pages/AdminPage';
import UserProfilePage from './pages/UserProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import LMRosterManagementPage from './pages/LMRosterManagementPage';
import LeagueLayout from './components/league/LeagueLayout';

// --- LAZY LOAD NEW SEASON-LONG PAGE COMPONENTS ---
const SeasonLongDashboardPage = lazy(() => import('./pages/SeasonLongDashboardPage'));
const CreateLeaguePage = lazy(() => import('./pages/CreateLeaguePage'));
const MockDraftSetupPage = lazy(() => import('./pages/MockDraftSetupPage'));
const JoinLeaguePage = lazy(() => import('./pages/JoinLeaguePage'));
const ClaimTeamPage = lazy(() => import('./pages/ClaimTeamPage'));
const MyTeamPage = lazy(() => import('./pages/MyTeamPage'));
const LeaguePage = lazy(() => import('./pages/LeaguePage'));
const DraftRoomPage = lazy(() => import('./pages/DraftRoomPage'));
const LeagueSchedulePage = lazy(() => import('./pages/LeagueSchedulePage'));
const LeaguePlayersPage = lazy(() => import('./pages/LeaguePlayersPage'));
const LeagueH2HMatchupPage = lazy(() => import('./pages/LeagueH2HMatchupPage'));
const LeagueSetupPage = lazy(() => import('./pages/LeagueSetupPage'));
const LMSetWaiverOrderPage = lazy(() => import('./pages/LMSetWaiverOrderPage'));
const CommishHQPage = lazy(() => import('./pages/CommishHQPage'));
const LegacyCommishSettingsRedirect = lazy(() => import('./pages/LegacyCommishSettingsRedirect'));
const HomePage = lazy(() => import('./pages/HomePage'));
const LeagueNotFoundPage = lazy(() => import('./pages/LeagueNotFoundPage'));
// Forum is parked: the page and backend endpoints still exist but the route is
// disabled because the feature is not surfaced anywhere in the live UI and we
// don't yet have the UGC moderation surface (report / block) that Apple
// requires for forum-style content. Re-enable when those are built.
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));

// --- LAZY LOAD CLASSIC MODE PAGE COMPONENTS ---
// Classic mode DARK (owner call 2026-07-20): materially a game for the
// anti-persona. Pages stay in the repo; routes + these imports disabled
// together (worker beat tasks also off - see celery_app.py). Re-enable =
// uncomment here and the routes below.
// const ClassicDashboardPage = lazy(() => import('./pages/ClassicDashboardPage'));
// const ClassicSquadBuilderPage = lazy(() => import('./pages/ClassicSquadBuilderPage'));
// const ClassicMyTeamPage = lazy(() => import('./pages/ClassicMyTeamPage'));
// const ClassicTransfersPage = lazy(() => import('./pages/ClassicTransfersPage'));
// const ClassicGameweekPage = lazy(() => import('./pages/ClassicGameweekPage'));
// const ClassicStandingsPage = lazy(() => import('./pages/ClassicStandingsPage'));

// --- GlobalLayout Component ---
const GlobalLayout: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, logout, user: authUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const toggleUserDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserDropdownOpen(prev => !prev);
  };
  const closeUserDropdown = () => { setIsUserDropdownOpen(false); };

  useEffect(() => {
    const handlePointerDown = () => { if (isUserDropdownOpen) closeUserDropdown(); };
    if (isUserDropdownOpen) window.addEventListener('pointerdown', handlePointerDown);
    return () => { window.removeEventListener('pointerdown', handlePointerDown); };
  }, [isUserDropdownOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsUserDropdownOpen(false); };
    if (isUserDropdownOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isUserDropdownOpen]);

  // GlobalLayout sits above route params. Resolve both detail and builder
  // contexts from the pathname so contest navigation remains available while
  // a lineup is being built or edited.
  const dfsContestIdentifierInPath = getDfsContestIdentifierFromPath(pathname);
  const dfsBuilderIdentifierInPath = getDfsBuilderIdentifierFromPath(pathname);
  // `today`/`next` are valid builder aliases but not contest-detail aliases.
  // Only expose a builder destination to the contextual nav once it is a
  // concrete contest id, so none of its links can lead to /contest/today.
  const navigableBuilderIdentifier = dfsBuilderIdentifierInPath && /^\d+$/.test(dfsBuilderIdentifierInPath)
    ? dfsBuilderIdentifierInPath
    : null;
  const contestIdentifierInPath = dfsContestIdentifierInPath ?? navigableBuilderIdentifier;
  const isDfsContestDetail = dfsContestIdentifierInPath !== null;
  const isDfsBuilder = pathname.startsWith('/dfs/build/');
  const isInDfsContest = isDfsContestDetail || isDfsBuilder;
  const isInLeague = pathname.startsWith('/leagues/');
  const isDraftRoom = /^\/leagues\/\d+\/draft\/?$/.test(pathname);
  const isLegalPage = pathname === '/support' || pathname === '/privacy';
  const showBackButton = isInDfsContest || isInLeague || isLegalPage;

  const handleBackClick = () => {
    // If a page has registered an interceptor (e.g. open drawer), call it first
    if (typeof (window as any).__backButtonInterceptor === 'function') {
      const handled = (window as any).__backButtonInterceptor();
      if (handled) return;
    }
    if (isDfsBuilder && navigableBuilderIdentifier) {
      navigate(buildDfsContestSectionHref(navigableBuilderIdentifier, 'leaderboard'));
    } else if (isDfsBuilder || isDfsContestDetail) navigate('/contests');
    else if (isInLeague) navigate('/season-long/dashboard');
    else if (isLegalPage) {
      const returnTo = (location.state as { from?: unknown } | null)?.from;
      navigate(typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//')
        ? returnTo
        : '/contests');
    }
    else navigate(-1);
  };

  const renderBottomNav = () => {
    if (contestIdentifierInPath) {
      return <DfsContestBottomNav contestIdOrAlias={contestIdentifierInPath} />;
    }
    // An evergreen builder alias has not resolved a concrete contest
    // yet, so contest-section links would be invalid. Its builder still owns
    // the screen until metadata resolves or the user backs out.
    if (isDfsBuilder) {
      return null;
    }
    // Inside a league, the contextual league nav is rendered by LeagueLayout
    // (bottom bar on mobile, top sub-nav on desktop), so don't render a bar here.
    if (isInLeague) {
      return null;
    }
    return <GeneralBottomNav />;
  };

  const isNavLinkActive = (pathPrefixes: string[]) => pathPrefixes.some(prefix => pathname.startsWith(prefix));
  const isContestsActive = isNavLinkActive(['/contests', '/dfs']);
  const isHomeActive = pathname === '/home';
  const isInsideSpecificLeague = pathname.startsWith('/leagues/');
  // GlobalLayout renders outside the league <Route>, so :leagueId never
  // resolves via useParams here - parse it from the path for the top tabs.
  const leagueIdInPath = isInsideSpecificLeague
    ? Number(pathname.match(/^\/leagues\/(\d+)/)?.[1] ?? NaN)
    : NaN;

  // Shared class tokens for interaction consistency (visual only)
  const navBtnBase =
    'font-heading font-bold text-sm px-3 py-2 rounded-md transition-colors duration-150 hover:bg-primary/50 hover:text-accent-yellow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card';

  const iconBtnBase =
    'p-2 rounded-full transition-colors duration-150 hover:bg-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card';

  const menuItemBase =
    'block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-primary/60 hover:text-accent-yellow transition-colors';

  return (
    <div className="min-h-screen bg-primary flex flex-col relative">
      {/* Stadium background overlay */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: "url('/la-bombonera.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'grayscale(100%) brightness(0.6)',
          opacity: 0.05,
        }}
      />
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{
          top: 'max(0.75rem, calc(env(safe-area-inset-top) + 0.5rem))',
          left: 'max(0.5rem, env(safe-area-inset-left))',
          right: 'max(0.5rem, env(safe-area-inset-right))',
        }}
        toastOptions={{
          className: '!rounded-lg !border !border-gray-700 !shadow-lg !shadow-black/20',
          duration: 5000,
          style: { background: '#102240', color: '#E0E0E0', border: '1px solid #4A5568' },
          success: { duration: 3000, iconTheme: { primary: '#48BB78', secondary: '#102240' } },
          error: { duration: 6000, iconTheme: { primary: '#F56565', secondary: '#102240' } }
        }}
      />

      {/* Sticky top nav — single depth (shadow), no extra border. The mobile
          contest scorecard supplies its own contextual header; desktop keeps
          the global nav.
          pt is max(safe-area, 1rem) so the nav extends into the iOS dynamic
          island area and its content always clears it, regardless of body
          padding or keyboard reflow. */}
      <nav className={`${isDraftRoom || isDfsContestDetail ? 'hidden lg:block' : ''} bg-surface-card text-accent-light-blue px-4 pb-4 pt-[max(env(safe-area-inset-top),1rem)] shadow-md sticky top-0 z-50`}>
        <div className="container mx-auto flex justify-between items-center">
          {/* --- Mobile-Only Top Bar --- */}
          <div className="md:hidden flex justify-between items-center w-full">
            <div className="w-1/4">
              {showBackButton && (
                <button
                  onClick={handleBackClick}
                  className={`${iconBtnBase} -ml-2`}
                  aria-label="Go back"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
            </div>

            <div className="w-1/2 text-center">
              <Link
                to={isAuthenticated ? "/home" : "/contests"}
                className="whitespace-nowrap text-accent-yellow font-heading font-bold text-lg min-[360px]:text-xl transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card"
              >
                <VenueCoBrand variant="mobile" />
              </Link>
            </div>

            <div className="w-1/4 flex justify-end">
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={toggleUserDropdown}
                    className={`${iconBtnBase} ${isNavLinkActive(['/profile', '/admin']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                    aria-haspopup="menu"
                    aria-expanded={isUserDropdownOpen}
                    aria-label="User menu"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {isUserDropdownOpen && (
                    <div
                      className="absolute right-0 mt-2 w-48 bg-primary rounded-md border border-gray-700/60 shadow-lg shadow-black/25 backdrop-blur py-1 z-50"
                      role="menu"
                      aria-label="User menu"
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-700/60">{authUser?.username}</div>
                      <Link to="/profile" className={menuItemBase} onClick={closeUserDropdown} role="menuitem">My Profile</Link>
                      {authUser?.is_superuser && (
                        <Link to="/admin" className={menuItemBase} onClick={closeUserDropdown} role="menuitem">Admin</Link>
                      )}
                      <button
                        onClick={() => { logout(); closeUserDropdown(); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-red-700 hover:text-white transition-colors"
                        role="menuitem"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="bg-accent-yellow text-primary font-heading font-bold text-xs px-3 py-1.5 rounded-md hover:bg-yellow-500 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card"
                >
                  LOGIN
                </Link>
              )}
            </div>
          </div>

          {/* --- Desktop-Only Top Bar --- */}
          <div className="hidden md:flex justify-between items-center w-full">
            <Link
              to={isAuthenticated ? "/home" : "/contests"}
              className="text-accent-yellow font-heading font-bold text-xl transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card"
            >
              <VenueCoBrand variant="desktop" />
            </Link>

            {isAuthenticated ? (
              <div className="flex items-center space-x-2 lg:space-x-3">
                {/* In-league (owner spec): the bar IS the league nav - tabs
                    RIGHT-ALIGNED with the profile icon, same position as the
                    standard bar's links (owner: centered was undiscoverable).
                    Contests/My Leagues live in the menu; the league name is gone
                    from chrome (dashboard + League home page only). lg+ only:
                    below that, the mobile bottom bar is the league nav. */}
                {isInsideSpecificLeague && Number.isFinite(leagueIdInPath) && (
                  <div className="hidden lg:flex">
                    <LeagueTopTabs leagueId={leagueIdInPath} />
                  </div>
                )}
                {!isInsideSpecificLeague && (
                  <>
                    <Link
                      to="/home"
                      className={`${navBtnBase} ${isHomeActive ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                    >
                      HOME
                    </Link>
                    <Link
                      to="/contests"
                      className={`${navBtnBase} ${isContestsActive ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                    >
                      CONTESTS
                    </Link>
                    <Link
                      to="/season-long/dashboard"
                      className={`${navBtnBase} ${isNavLinkActive(['/season-long', '/leagues']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                    >
                      LEAGUES
                    </Link>
                    <Link
                      to="/scoring"
                      className={`${navBtnBase} ${isNavLinkActive(['/scoring']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                    >
                      HOW TO PLAY
                    </Link>
                    <Link
                      to="/stats"
                      className={`${navBtnBase} ${isNavLinkActive(['/stats']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                    >
                      STATS
                    </Link>
                    {/* Blog de-surfaced from nav (owner 2026-07-22) - the
                        /blog route remains reachable by URL. */}
                  </>
                )}

                <div className="relative">
                  <button
                    onClick={toggleUserDropdown}
                    className={
                      isInsideSpecificLeague
                        ? `${iconBtnBase} ${isNavLinkActive(['/profile', '/admin']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`
                        : `${navBtnBase} ${isNavLinkActive(['/profile', '/admin']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'} flex items-center`
                    }
                    aria-haspopup="menu"
                    aria-expanded={isUserDropdownOpen}
                    aria-label="Account menu"
                  >
                    {isInsideSpecificLeague ? (
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" />
                      </svg>
                    ) : (
                      <>
                        {authUser?.username || 'Menu'}
                        <svg
                          className={`w-4 h-4 ml-1 transition-transform duration-200 ${isUserDropdownOpen ? 'rotate-180' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </>
                    )}
                  </button>

                  {isUserDropdownOpen && (
                    <div
                      className="absolute right-0 mt-2 w-48 bg-primary rounded-md border border-gray-700/60 shadow-lg shadow-black/25 backdrop-blur py-1 z-50"
                      role="menu"
                      aria-label="User menu"
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <Link to="/profile" className={menuItemBase} onClick={closeUserDropdown} role="menuitem">My Profile</Link>
                      {authUser?.is_superuser && (
                        <Link to="/admin" className={menuItemBase} onClick={closeUserDropdown} role="menuitem">Admin Panel</Link>
                      )}
                      {isInsideSpecificLeague && (
                        <>
                          <div className="border-t border-gray-700/60 my-1" />
                          <Link to="/contests" className={menuItemBase} onClick={closeUserDropdown} role="menuitem">Contests</Link>
                          <Link to="/season-long/dashboard" className={menuItemBase} onClick={closeUserDropdown} role="menuitem">My Leagues</Link>
                          <Link to="/scoring" className={menuItemBase} onClick={closeUserDropdown} role="menuitem">How to Play</Link>
                          <Link to="/stats" className={menuItemBase} onClick={closeUserDropdown} role="menuitem">Stats</Link>
                          <div className="border-t border-gray-700/60 my-1" />
                        </>
                      )}
                      <button
                        onClick={() => { logout(); closeUserDropdown(); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-red-700 hover:text-white transition-colors"
                        role="menuitem"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/contests"
                  className={`${navBtnBase} ${isContestsActive ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                >
                  CONTESTS
                </Link>
                {/* Logged-out LEAGUES lands on the protected dashboard ->
                    login redirect with the intended path preserved - the
                    standard signup on-ramp (owner 2026-07-22: nav parity
                    with the logged-in shell; Blog de-surfaced). */}
                <Link
                  to="/season-long/dashboard"
                  className={`${navBtnBase} ${isNavLinkActive(['/season-long', '/leagues']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                >
                  LEAGUES
                </Link>
                <Link
                  to="/scoring"
                  className={`${navBtnBase} ${isNavLinkActive(['/scoring']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                >
                  HOW TO PLAY
                </Link>
                <Link
                  to="/stats"
                  className={`${navBtnBase} ${isNavLinkActive(['/stats']) ? 'bg-accent-yellow text-primary' : 'text-accent-light-blue'}`}
                >
                  STATS
                </Link>
                <div className="border-l border-gray-600 h-6 mx-2" />
                <Link
                  to="/login"
                  className="bg-accent-yellow text-primary font-heading font-bold text-sm px-3 py-2 rounded-md hover:bg-yellow-500 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card"
                >
                  LOGIN
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Content wrapper — contest detail owns its top edge through its contextual header. */}
      <div className={`flex-grow ${isDfsContestDetail ? 'pt-0' : 'pt-3 md:pt-0'} flex flex-col min-h-0 relative z-10 [&>*]:!bg-transparent ${isDfsBuilder || isInLeague ? 'pb-0' : 'pb-[var(--app-mobile-bottom-nav-height)] md:pb-0'}`}>
        <Suspense
          fallback={
            <div className="flex justify-center items-center h-[calc(100vh-100px)]" aria-busy="true" aria-live="polite">
              <Spinner size="lg" />
              <span className="sr-only">Loading content…</span>
            </div>
          }
        >
          {children}
        </Suspense>
      </div>

      {/* Bottom nav stays mobile-only, unchanged logic */}
      <div className="block md:hidden relative z-10">
        {renderBottomNav()}
      </div>
    </div>
  );
};

// --- ProtectedRoute Component ---
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  // noindex every auth-gated URL regardless of auth state so crawlers that hit these
  // URLs directly pick up the directive before the client-side redirect to /login.
  const seo = (
    <SEO
      title="Fantasy Futbol"
      description="Fantasy soccer for the Premier League."
      path={location.pathname}
      noindex
    />
  );
  if (isLoading) {
    return (
      <>
        {seo}
        <div className="flex justify-center items-center min-h-screen bg-primary text-accent-light-blue text-lg">
          Authenticating...
        </div>
      </>
    );
  }
  if (!isAuthenticated) {
    saveIntendedPath(location.pathname + location.search + location.hash);
    return (
      <>
        {seo}
        <Navigate to="/login" state={{ from: location }} replace />
      </>
    );
  }
  return (
    <>
      {seo}
      {children}
    </>
  );
};

// --- App Function ---
function App() {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    mixpanelTrack('Page Viewed', {
      'Page Name': location.pathname,
      'Page Title': document.title,
      'Is Authenticated': isAuthenticated
    });
  }, [location.pathname, isAuthenticated, user]);

  // Reset scroll to top on forward navigation (link clicks, navigate() calls)
  // but skip on browser back/forward ('POP') so the browser can restore the
  // previous scroll position the way users expect.
  useEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [location.pathname, navigationType]);

  return (
    <ConfirmDialogProvider>
    <GlobalLayout>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/scoring" element={<ScoringInfoPage />} />
        <Route path="/film-room" element={<FilmRoomPage />} />
        <Route path="/film-room/:chapterSlug" element={<FilmRoomPage />} />
        <Route path="/stats" element={<PublicPlayerStatsPage />} /> {/* Public player stats */}
        <Route path="/players" element={<Navigate to="/stats" replace />} /> {/* legacy redirect */}
        <Route path="/game-logs" element={<GameLogLeaderboardPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/contests" element={<ContestsPage />} />
        <Route path="/contest-rules" element={<ContestRulesPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/bar/:ref" element={<BarLandingPage />} /> {/* bar QR smart link */}
        {/* Partner bars' own contests (unlisted; BAR-CONTESTS-SCOPE-2026-10-06.md) */}
        {VENUE_SLUGS.map(slug => (
          <Route key={slug} path={`/${slug}`} element={<VenuePage key={slug} slug={slug} />} />
        ))}
        <Route path="/dfs/upcoming" element={<Navigate to="/contests" replace />} />
        <Route path="/dfs/build/:contestIdOrAlias" element={<DfsLineupBuilderPage />} />
        <Route path="/dfs/contest/:contestIdentifier" element={<DfsContestPage />} />
        <Route path="/dfs/build" element={<Navigate to="/contests" replace />} />
        <Route path="/dfs/contest" element={<Navigate to="/contests" replace />} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
        {/* Authenticated HQ and the ordinary post-login destination. */}
        <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        {/* /forum route disabled — see ForumPage import comment above. */}
        {/* /classic/* routes DARK (owner 2026-07-20) — see the disabled
            lazy imports above for the rationale and re-enable steps. */}
        {/* Public acquisition surfaces on web and native. Persistence still
            requires auth at the action boundary; private league data below
            remains protected. */}
        <Route path="/season-long/dashboard" element={<SeasonLongDashboardPage />} />
        <Route path="/season-long/create-league" element={<CreateLeaguePage />} />
        <Route path="/season-long/mock-draft" element={<ProtectedRoute><MockDraftSetupPage /></ProtectedRoute>} />
        <Route path="/season-long/join-league" element={<ProtectedRoute><JoinLeaguePage /></ProtectedRoute>} />
        <Route path="/season-long/claim-team" element={<ProtectedRoute><ClaimTeamPage /></ProtectedRoute>} />
        <Route path="/leagues/:leagueId" element={<ProtectedRoute><LeagueLayout /></ProtectedRoute>} >
          <Route index element={<Navigate to="my-team" replace />} />
          <Route path="setup" element={<LeagueSetupPage />} />
          <Route path="my-team" element={<MyTeamPage />} />
          <Route path="my-team/:teamId" element={<MyTeamPage />} />
          <Route path="my-team/week/:weekNumber" element={<MyTeamPage />} />
          <Route path="my-team/:teamId/week/:weekNumber" element={<MyTeamPage />} />
          <Route path="teams/:teamId" element={<MyTeamPage />} />
          <Route path="teams/:teamId/week/:weekNumber" element={<MyTeamPage />} />
          <Route path="matchup" element={<LeagueH2HMatchupPage />} />
          <Route path="matchup/week/:weekNumber" element={<LeagueH2HMatchupPage />} />
          <Route path="league" element={<LeaguePage />} />
          <Route path="commish" element={<CommishHQPage />} />
          {/* The same page in read-only mode, for members. The role,
              not the path, decides what renders. */}
          <Route path="settings" element={<CommishHQPage />} />
          <Route path="schedule" element={<LeagueSchedulePage />} />
          <Route path="players" element={<LeaguePlayersPage />} /> {/* League-specific player pool */}
          <Route path="admin/roster-management" element={<LMRosterManagementPage />} />
          <Route path="admin/set-initial-waiver-order" element={<LMSetWaiverOrderPage />} />
          <Route path="admin/settings" element={<LegacyCommishSettingsRedirect />} />
          <Route path="draft" element={<DraftRoomPage />} />
          <Route path="*" element={<LeagueNotFoundPage />} />
        </Route>
        <Route path="/" element={<RootRedirector />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </GlobalLayout>
    </ConfirmDialogProvider>
  );
}

export default App;
